import React, { useState, useEffect, useRef, useCallback } from "react";
import { Link } from "react-router-dom";
import { analytics } from "./utils/analytics";
import "./CalculatorScreen.css";

const gradeGPA = (score) => {
  if (score >= 90) return { grade: "A+", gpa: 4.0 };
  if (score >= 85) return { grade: "A", gpa: 4.0 };
  if (score >= 80) return { grade: "A-", gpa: 3.8 };
  if (score >= 75) return { grade: "B+", gpa: 3.4 };
  if (score >= 71) return { grade: "B", gpa: 3.0 };
  if (score >= 68) return { grade: "B-", gpa: 2.8 };
  if (score >= 64) return { grade: "C+", gpa: 2.4 };
  if (score >= 61) return { grade: "C", gpa: 2.0 };
  if (score >= 57) return { grade: "C-", gpa: 1.8 };
  if (score >= 53) return { grade: "D+", gpa: 1.4 };
  if (score >= 45) return { grade: "D", gpa: 1.0 };
  return { grade: "F", gpa: 0.0 };
};

const getGpaMessage = (gpa) => {
  if (gpa > 3.4) return "Excellent standing — keep this momentum.";
  if (gpa > 3.0) return "Strong semester — you're in a solid range.";
  if (gpa > 2.5) return "Decent result — a little focus can lift this further.";
  if (gpa > 2.0) return "Passing zone — plan the next improvement carefully.";
  return "Room to recover — use the next attempt wisely.";
};

const CalculatorScreen = () => {
  const [numCourses, setNumCourses] = useState(null);
  const [scores, setScores] = useState([]);
  const [currentScore, setCurrentScore] = useState("");
  const [started, setStarted] = useState(false);
  const hasTrackedGPA = useRef(false);
  const inputRef = useRef(null);

  const calculateGPA = useCallback((scoreArray = scores) => {
    if (scoreArray.length === 0) return "0.00";
    const totalGPA = scoreArray.reduce(
      (acc, score) => acc + gradeGPA(score).gpa,
      0
    );
    return (totalGPA / scoreArray.length).toFixed(2);
  }, [scores]);

  const handleNextScore = () => {
    const score = parseInt(currentScore, 10);
    if (!isNaN(score) && score >= 0 && score <= 100) {
      setScores((prev) => [...prev, score]);
      setCurrentScore("");
    }
  };

  const handleStart = () => {
    if (numCourses && numCourses > 0 && numCourses <= 20) {
      analytics.trackCoursesEntered(numCourses);
      setStarted(true);
    }
  };

  const handleKeyDown = (event, action) => {
    if (event.key === "Enter") {
      event.preventDefault();
      action();
    }
  };

  useEffect(() => {
    if (started && scores.length === numCourses && numCourses > 0 && !hasTrackedGPA.current) {
      const finalGPA = calculateGPA();
      analytics.trackGPACalculated(finalGPA, numCourses);
      hasTrackedGPA.current = true;
    }
  }, [started, scores.length, numCourses, calculateGPA]);

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus();
    }
  }, [started, scores.length]);

  const handleReset = () => {
    setNumCourses(null);
    setScores([]);
    setCurrentScore("");
    setStarted(false);
    hasTrackedGPA.current = false;
  };

  const previewScore = parseInt(currentScore, 10);
  const preview =
    !isNaN(previewScore) && previewScore >= 0 && previewScore <= 100
      ? gradeGPA(previewScore)
      : null;

  const showResults = started && numCourses && scores.length === numCourses;
  const progress = !started
    ? 0
    : showResults
      ? 100
      : Math.round((scores.length / numCourses) * 100);

  return (
    <div className="calc-page">
      <header className="calc-top">
        <Link to="/" className="calc-back">
          ← Home
        </Link>
        <div className="calc-top__brand">
          <span className="calc-top__name">UBIT GPA Calculator</span>
          <span className="calc-top__sub">University of Karachi · KU / UoK</span>
        </div>
      </header>

      <div className="calc-shell">
        <div className="calc-progress" aria-hidden={!started}>
          <div className="calc-progress__track">
            <div
              className="calc-progress__fill"
              style={{ width: `${progress}%` }}
            />
          </div>
          <p className="calc-progress__label">
            {!started
              ? "Step 1 of 2 · Courses"
              : showResults
                ? "Complete"
                : `Course ${scores.length + 1} of ${numCourses}`}
          </p>
        </div>

        <div className="calc-panel">
          {!started ? (
            <div className="calc-step">
              <h1>How many courses?</h1>
              <p className="calc-step__hint">
                Enter the number of courses for this semester (1–20).
              </p>
              <label className="calc-label" htmlFor="course-count">
                Total courses
              </label>
              <input
                id="course-count"
                ref={inputRef}
                type="number"
                min="1"
                max="20"
                inputMode="numeric"
                value={numCourses || ""}
                onChange={(e) =>
                  setNumCourses(parseInt(e.target.value, 10) || null)
                }
                onKeyDown={(e) => handleKeyDown(e, handleStart)}
                className="calc-input"
                placeholder="e.g. 6"
              />
              <button
                type="button"
                onClick={handleStart}
                className="calc-btn"
                disabled={!numCourses || numCourses < 1 || numCourses > 20}
              >
                Continue
              </button>
            </div>
          ) : !showResults ? (
            <div className="calc-step">
              <h1>Score for course {scores.length + 1}</h1>
              <p className="calc-step__hint">
                Enter marks out of 100. Grade points update using the KU scale.
              </p>
              <label className="calc-label" htmlFor="course-score">
                Marks (0–100)
              </label>
              <input
                id="course-score"
                ref={inputRef}
                type="number"
                min="0"
                max="100"
                inputMode="numeric"
                value={currentScore}
                onChange={(e) => setCurrentScore(e.target.value)}
                onKeyDown={(e) => handleKeyDown(e, handleNextScore)}
                className="calc-input"
                placeholder="e.g. 78"
              />

              {preview && (
                <div className="calc-preview" aria-live="polite">
                  <span>
                    Grade <strong>{preview.grade}</strong>
                  </span>
                  <span>
                    Points <strong>{preview.gpa.toFixed(1)}</strong>
                  </span>
                </div>
              )}

              <button
                type="button"
                onClick={handleNextScore}
                disabled={currentScore === "" || preview === null}
                className="calc-btn"
              >
                {scores.length + 1 === numCourses ? "See results" : "Next course"}
              </button>

              {scores.length > 0 && (
                <div className="calc-live">
                  Running GPA: <strong>{calculateGPA(scores)}</strong>
                  <span>
                    ({scores.length}/{numCourses} entered)
                  </span>
                </div>
              )}
            </div>
          ) : (
            <div className="calc-results">
              <p className="calc-results__eyebrow">Semester estimate</p>
              <h1 className="calc-results__gpa">{calculateGPA()}</h1>
              <p className="calc-results__message">
                {getGpaMessage(parseFloat(calculateGPA()))}
              </p>

              <div className="calc-table" role="table" aria-label="Course GPA breakdown">
                <div className="calc-table__head" role="row">
                  <span role="columnheader">Course</span>
                  <span role="columnheader">Score</span>
                  <span role="columnheader">Grade</span>
                  <span role="columnheader">GPA</span>
                </div>
                {scores.map((score, index) => {
                  const result = gradeGPA(score);
                  return (
                    <div className="calc-table__row" role="row" key={`course-${index}`}>
                      <span role="cell">{index + 1}</span>
                      <span role="cell">{score}</span>
                      <span role="cell">{result.grade}</span>
                      <span role="cell">{result.gpa.toFixed(1)}</span>
                    </div>
                  );
                })}
              </div>

              <button type="button" onClick={handleReset} className="calc-btn">
                Calculate again
              </button>
              <Link to="/" className="calc-secondary-link">
                Back to home
              </Link>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CalculatorScreen;
